export type HelpVideoProvider = 'youtube';

export type HelpVideo = {
  provider: HelpVideoProvider;
  id: string;
  duration?: number;
};

export type HelpArticleFrontmatter = {
  title: string;
  category: string;
  summary: string;
  keywords?: string[];
  video?: HelpVideo;
  relatedSkills?: string[];
  relatedArticles?: string[];
  updated: string;
  /**
   * Written, but not announced yet.
   *
   * A draft article is reachable at its own URL and nowhere else: it is left
   * out of category listings, search, the sitemap and the in-chat
   * `find_help_article` tool. It exists for features that ship behind a launch
   * flag, where the help has to land in the same PR as the code (see CLAUDE.md)
   * but publishing it would advertise something nobody can use yet.
   *
   * Drop the flag at launch. That is the whole of the publish step.
   */
  draft?: boolean;
};

export type HelpArticle = HelpArticleFrontmatter & {
  slug: string;
  url: string;
  body: string;
};

export type HelpCategory = {
  slug: string;
  title: string;
  description: string;
};

export const HELP_CATEGORIES: HelpCategory[] = [
  {
    slug: 'getting-started',
    title: 'Getting started',
    description: 'The basics. Set up your business and send your first quote.',
  },
  {
    slug: 'quotes',
    title: 'Quotes',
    description: 'Drafting, sending, accepting and converting quotes.',
  },
  {
    slug: 'invoices',
    title: 'Invoices',
    description: 'Raising invoices, recording payments and chasing what’s late.',
  },
  {
    slug: 'clients',
    title: 'Clients',
    description: 'Adding clients, viewing history and managing details.',
  },
  {
    slug: 'schedule',
    title: 'Schedule',
    description: 'Booking jobs, managing the diary and recurring work.',
  },
  {
    slug: 'gas-certs',
    title: 'Certificates',
    description: 'Gas, oil and air conditioning certificates, from the job to the customer: CP12s, OFTEC reports, F-Gas records and more.',
  },
  {
    slug: 'service-reminders',
    title: 'Service Reminders',
    description: 'Tracking recurring services, reminding customers and booking the work back in.',
  },
  {
    slug: 'service-plans',
    title: 'Service Plans',
    description: 'Monthly cover plans, what each client is entitled to and what the book is worth.',
  },
  {
    slug: 'engineers',
    title: 'Engineers',
    description: 'Adding your team, working hours and permissions.',
  },
  {
    slug: 'reviews',
    title: 'Reviews',
    description: 'Collecting Google, Trustpilot and Checkatrade reviews, and replying to them.',
  },
  {
    slug: 'ai',
    title: 'Mucka AI',
    description: 'Talking to Mucka, approving suggestions and finding things fast.',
  },
  {
    slug: 'settings',
    title: 'Settings',
    description: 'Passwords, business defaults, Mucka voice and account preferences.',
  },
  {
    slug: 'mobile',
    title: 'Mobile app',
    description: 'Install Mucka on your phone, enable notifications and use Mucka on the go.',
  },
  {
    slug: 'integrations',
    title: 'Integrations',
    description: 'WhatsApp, phone, calendars and other things Mucka plugs into.',
  },
  {
    slug: 'billing',
    title: 'Billing',
    description: 'Plans, payment details and subscription management.',
  },
  {
    slug: 'troubleshooting',
    title: 'Troubleshooting',
    description: 'When something’s not working, start here.',
  },
];

export function getCategory(slug: string): HelpCategory | undefined {
  return HELP_CATEGORIES.find((c) => c.slug === slug);
}
